// src/pages/admin/AdminReview.jsx
import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabaseClient'
import { adminListJobs, adminListApplications } from '../../services/jobs'

export default function AdminReview() {
  const [pendingUsers, setPendingUsers] = useState([])
  const [processedUsers, setProcessedUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('') // '', 'pharmacist', 'pharmacy', 'admin'
  const [activeTab, setActiveTab] = useState('pending') // 'pending' | 'processed'
  const [noteDrafts, setNoteDrafts] = useState({}) // { [user_id]: '메모 내용' }

  // 🔹 공고/매칭 관련 상태
  const [jobStats, setJobStats] = useState({ total: 0, open: 0, closed: 0 })
  const [applications, setApplications] = useState([]) // 전체 매칭(지원) 목록
  const [selectedApplication, setSelectedApplication] = useState(null) // 매칭 상세 보기


  const loadAll = useCallback(async () => {
    setLoading(true)
    setError('')
    setSelectedApplication(null)

    try {
      // 1) 가입 승인 - 대기 중 (profiles만 조회)
      const { data: pending, error: errPending } = await supabase
        .from('profiles')
        .select('user_id, email, display_name, role, approval_status, approval_note, created_at, approved_at, phone')
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: true })

      if (errPending) {
        console.error('loadPending error', errPending)
        setError(errPending.message)
        setPendingUsers([])
      }

      // 2) 가입 승인 - 처리 완료 (approved / rejected)
      const { data: processed, error: errProcessed } = await supabase
        .from('profiles')
        .select('user_id, email, display_name, role, approval_status, approval_note, created_at, approved_at, phone')
        .neq('approval_status', 'pending')
        .order('approved_at', { ascending: false })

      if (errProcessed) {
        console.error('[AdminReview] loadProcessed error:', errProcessed)
        setError((prev) => prev || errProcessed.message)
        setProcessedUsers([])
      }

      // 3) 상세 데이터 (registrations) 별도 조회하여 이메일로 매핑
      const { data: regs, error: regsError } = await supabase.from('registrations').select('*')
      if (regsError) console.error('registrations load error', regsError)

      const regMap = {}
      if (regs && Array.isArray(regs)) {
        regs.forEach(r => {
          if (r.email) regMap[r.email.toLowerCase()] = r
        })
      }

      const mergeReg = (u) => ({
        ...u,
        registrations: u.email ? regMap[u.email.toLowerCase()] : null
      })

      setProcessedUsers((processed || []).map(mergeReg))
      setPendingUsers((pending || []).map(mergeReg))

      // 3) 🔹 전체 공고( job_posts ) 현황
      try {
        const { data: jobs, error: jobsError } = await adminListJobs()
        if (jobsError) {
          console.error('adminListJobs error', jobsError)
          setJobStats({ total: 0, open: 0, closed: 0 })
        } else if (jobs && Array.isArray(jobs)) {
          const total = jobs.length
          const open = jobs.filter((j) => j.status === 'open').length
          const closed = jobs.filter((j) => j.status === 'closed').length
          setJobStats({ total, open, closed })
        } else {
          setJobStats({ total: 0, open: 0, closed: 0 })
        }
      } catch (e) {
        console.error('adminListJobs exception', e)
        setJobStats({ total: 0, open: 0, closed: 0 })
      }

      // 4) 🔹 전체 매칭( applications ) 목록
      try {
        const { data: apps, error: appsError } = await adminListApplications()
        if (appsError) {
          console.error('adminListApplications error', appsError)
          setApplications([])
        } else {
          setApplications(apps || [])
        }
      } catch (e) {
        console.error('adminListApplications exception', e)
        setApplications([])
      }

    } catch (criticalError) {
      console.error('[AdminReview] Critical load error:', criticalError)
      setError('데이터를 불러오는 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  // 승인 / 반려 공통 로직 (API 사용: 메타데이터 + 프로필 동기화)
  async function updateApproval(user_id, status, newRole = null) {
    const note = noteDrafts[user_id] || null
    console.log(`[AdminReview] updateApproval start: user=${user_id}, status=${status}, role=${newRole}`)

    let updateSuccess = false

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token

      if (!token) {
        alert('로그인 세션이 만료되었습니다.')
        return
      }

      const response = await fetch('/api/admin/approve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user_id,
          status,
          role: newRole,
          note
        })
      })

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.error || '승인 처리에 실패했습니다.')
      }

      console.log('[AdminReview] API update success')
      updateSuccess = true

    } catch (err) {
      console.error('[AdminReview] API Error:', err)
      alert(`오류 발생: ${err.message}`)
      return
    }

    // 3) 검증 및 UI 즉시 업데이트 (Optimistic Update)
    if (updateSuccess) {
      // (1) DB 검증 (선택적)
      const { data: verifyData, error: verifyError } = await supabase
        .from('profiles')
        .select('approval_status, role')
        .eq('user_id', user_id)
        .single()

      if (verifyError) {
        console.error('[AdminReview] Verification query failed:', verifyError)
      } else {
        if (verifyData.approval_status !== status) {
          alert(`⚠️ 데이터베이스 반영 지연이 감지되었습니다. (현재: ${verifyData.approval_status})`)
          // 여기서 return하지 않고 UI라도 먼저 업데이트해 줍니다.
        } else {
          console.log('[AdminReview] ✅ DB 업데이트 확인 완료')
        }
      }

      // (2) UI 상태 즉시 반영 (Optimistic Update with confirmed success)
      console.log('[AdminReview] Applying optimistic UI update...')

      // pending 목록에서 찾아서 제거하거나 업데이트
      setPendingUsers(prev => {
        const found = prev.find(u => u.user_id === user_id)
        if (found) {
          // 승인완료/거절됨 상태가 되면 pending 목록에서는 제거되어야 함
          // (만약 status가 'pending'으로 되돌아가는 기능이 있다면 로직이 다르겠지만, 보통 여기서 승인/반려만 함)
          if (status !== 'pending') {
            // processed 목록으로 이동시키기 위해 정보 캡처
            const updatedUser = {
              ...found,
              approval_status: status,
              role: newRole || found.role,
              approval_note: note,
              approved_at: new Date().toISOString()
            }

            setProcessedUsers(processed => [updatedUser, ...processed]) // 상단에 추가
            return prev.filter(u => u.user_id !== user_id)
          }
        }
        return prev
      })

      // processed 목록에 이미 있던 유저를 수정한 경우 (승인취소, 재승인 등)
      setProcessedUsers(prev => prev.map(u => {
        if (u.user_id === user_id) {
          return {
            ...u,
            approval_status: status,
            role: newRole || u.role,
            approval_note: note,
            approved_at: status === 'approved' ? new Date().toISOString() : u.approved_at
          }
        }
        return u
      }))

      // 메시지 초기화
      setNoteDrafts(prev => {
        const next = { ...prev }
        delete next[user_id]
        return next
      })

      // (3) 전체 리로드 (백그라운드에서 조용히 수행해도 되고, 생략해도 됨)
      // 여기서는 확실한 동기화를 위해 조금 딜레이 후 수행하거나 생략
      // 사용자 경험을 위해 리로딩 표시(spinner) 없이 조용히 넘어갑니다.
    }
  }

  const handleApprove = (user) => {
    if (!window.confirm(`"${user.display_name || user.email}" 사용자를 승인하시겠습니까?`)) return
    updateApproval(user.user_id, 'approved', user.role)
  }

  const handleReject = (user_id) => {
    if (
      !window.confirm(
        '이 사용자를 승인 거절(보류)하시겠습니까?\n※ 작성한 메모/사유가 함께 저장됩니다.'
      )
    )
      return
    updateApproval(user_id, 'rejected')
  }

  const roleLabel = (role) => {
    if (role === 'pharmacist') return '약사'
    if (role === 'pharmacy') return '약국'
    if (role === 'admin') return '관리자'
    return role || '-'
  }

  const applyFilters = (list) =>
    list.filter((u) => {
      const t = `${u.email ?? ''} ${u.display_name ?? ''} ${u.role ?? ''}`.toLowerCase()
      const matchSearch = search ? t.includes(search.toLowerCase()) : true
      const matchRole = roleFilter ? u.role === roleFilter : true
      return matchSearch && matchRole
    })

  const filteredPending = applyFilters(pendingUsers)
  console.log('[AdminReview] filteredPending:', filteredPending)
  const filteredProcessed = applyFilters(processedUsers)

  // 🔹 사용자 상세 보기
  const [selectedUser, setSelectedUser] = useState(null)

  const renderUserTable = (users, isProcessed = false) => {
    if (users.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <p className="text-sm text-gray-500">
            {isProcessed ? '최근에 처리된 가입 요청이 없습니다.' : '승인 대기 중인 사용자가 없습니다.'}
          </p>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-4">
        {/* 📱 Mobile: Card List View */}
        <div className="flex flex-col gap-3 md:hidden">
          {users.map((u) => (
            <div
              key={u.user_id}
              className={`p-4 rounded-xl border bg-white shadow-sm transition active:scale-[0.98] 
                 ${selectedUser?.user_id === u.user_id ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-100'}
               `}
              onClick={() => setSelectedUser(selectedUser?.user_id === u.user_id ? null : u)}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <span className={`inline-flex items-center rounded-md px-2 py-1 text-[10px] font-bold mb-1
                      ${u.role === 'pharmacist' ? 'bg-purple-50 text-purple-700' :
                      u.role === 'pharmacy' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                    {roleLabel(u.role)}
                  </span>
                  <div className="font-bold text-gray-900 text-sm">{u.display_name || '이름 없음'}</div>
                  <div className="text-xs text-gray-400">{u.email}</div>
                </div>
                {isProcessed ? (
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${u.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                    {u.approval_status === 'approved' ? '승인' : '거절'}
                  </span>
                ) : (
                  <button className="px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-lg border border-blue-100">
                    심사
                  </button>
                )}
              </div>
              <div className="text-[10px] text-gray-400 text-right">
                {isProcessed ? '처리: ' : '요청: '}
                {isProcessed
                  ? (u.approved_at ? new Date(u.approved_at).toLocaleDateString() : '-')
                  : (u.created_at ? new Date(u.created_at).toLocaleDateString() : '-')
                }
              </div>
            </div>
          ))}
        </div>

        {/* 💻 Desktop: Table View */}
        <div className="hidden md:block rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="border-b bg-gray-50/50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-6 py-3 text-left">이메일</th>
                  <th className="px-6 py-3 text-left">이름</th>
                  <th className="px-6 py-3 text-left">역할</th>
                  <th className="px-6 py-3 text-left">
                    {isProcessed ? '처리일' : '요청일'}
                  </th>
                  <th className="px-6 py-3 text-right">상태 / 관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => {
                  const displayName =
                    u.display_name ||
                    (u.email ? u.email.split('@')[0] : u.user_id?.slice(0, 8) + '…')

                  // 현재 선택된 유저인지 확인
                  const isSelected = selectedUser?.user_id === u.user_id

                  return (
                    <tr
                      key={u.user_id}
                      className={`transition-colors cursor-pointer ${isSelected ? 'bg-blue-50/60' : 'hover:bg-gray-50'}`}
                      onClick={() => setSelectedUser(isSelected ? null : u)}
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{u.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {displayName}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium 
                          ${u.role === 'pharmacist' ? 'bg-purple-100 text-purple-800' :
                            u.role === 'pharmacy' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                          {roleLabel(u.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-xs">
                        {isProcessed
                          ? (u.approved_at ? new Date(u.approved_at).toLocaleString() : '-')
                          : (u.created_at ? new Date(u.created_at).toLocaleString() : '-')
                        }
                      </td>
                      <td className="px-6 py-4 text-right">
                        {isProcessed ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${u.approval_status === 'approved'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-rose-100 text-rose-800'
                              }`}
                          >
                            {u.approval_status === 'approved' ? '승인됨' : '거절됨'}
                          </span>
                        ) : (
                          <button
                            className="text-blue-600 hover:text-blue-800 font-semibold text-xs border border-blue-200 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition"
                          >
                            심사하기
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 🔹 선택된 사용자 상세 정보 패널 (액션 포함) */}
        {selectedUser && (
          <div className="rounded-2xl border border-blue-100 bg-white shadow-lg animate-fade-in-up ring-4 ring-blue-50/50">
            {/* Header */}
            <div className="flex justify-between items-start p-6 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                  {selectedUser.display_name || '이름 없음'}
                  <span className={`px-2 py-0.5 rounded text-xs font-normal ${selectedUser.role === 'pharmacist' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {roleLabel(selectedUser.role)}
                  </span>
                </h3>
                <p className="text-sm text-gray-400 mt-1 font-mono">{selectedUser.email}</p>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-8 text-sm">
              {/* Left Column: Info */}
              <div className="lg:col-span-2 space-y-6">

                {/* 1. 증빙 자료 부터 강조 */}
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    제출 서류 (면허증)
                  </h4>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    {/* 약사 OR 약국 모두 면허증/사업자등록증 등 이미지가 있으면 표시 */}
                    {(selectedUser.role === 'pharmacist' || selectedUser.role === 'pharmacy') &&
                      (selectedUser.pharmacists?.license_url || selectedUser.registrations?.license_attachment_url) ? (
                      <div className="flex flex-col items-start gap-3">
                        <div className="relative group cursor-zoom-in w-full max-w-sm bg-white rounded-lg border shadow-sm overflow-hidden"
                          onClick={() => window.open(selectedUser.pharmacists?.license_url || selectedUser.registrations?.license_attachment_url, '_blank')}>
                          <img
                            src={selectedUser.pharmacists?.license_url || selectedUser.registrations?.license_attachment_url}
                            alt="면허증/증빙서류 사본"
                            className="w-full h-auto object-cover hover:opacity-95 transition"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 bg-black/60 text-white text-xs px-2 py-1 rounded">크게 보기</span>
                          </div>
                        </div>
                        <p className="text-xs text-blue-500 font-medium">✨ 식별 가능한지 확인해 주세요.</p>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-xs py-4 text-center border border-dashed rounded-lg bg-white">
                        등록된 이미지가 없습니다.
                      </p>
                    )}
                  </div>
                </div>

                {/* 2. 상세 정보 */}
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    회원 상세 정보
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div>
                      <span className="block text-xs font-semibold text-gray-500 mb-1">전화번호</span>
                      <span className="text-gray-900">{selectedUser.phone || '-'}</span>
                    </div>
                    {selectedUser.role === 'pharmacist' && (
                      <div>
                        <span className="block text-xs font-semibold text-gray-500 mb-1">면허 번호</span>
                        <span className="text-gray-900 font-mono tracking-wide">{selectedUser.pharmacists?.license_number || selectedUser.registrations?.license_number || '-'}</span>
                      </div>
                    )}
                    {selectedUser.role === 'pharmacy' && (
                      <div>
                        <span className="block text-xs font-semibold text-gray-500 mb-1">약국명</span>
                        <span className="text-gray-900">{selectedUser.pharmacies?.pharmacy_name || selectedUser.registrations?.pharmacy_name || '-'}</span>
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <span className="block text-xs font-semibold text-gray-500 mb-1">주소</span>
                      <span className="text-gray-900">{selectedUser.pharmacists?.address || selectedUser.pharmacies?.address || selectedUser.registrations?.pharmacy_address || selectedUser.registrations?.personal_address || '-'}</span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Right Column: Actions */}
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 h-fit space-y-6">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">관리자 메모</h4>
                  <p className="text-xs text-gray-500 mb-2">
                    승인/거절 사유나 특이사항을 기록하세요.
                  </p>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition bg-white"
                    rows={4}
                    placeholder="이곳에 메모를 입력하세요..."
                    value={noteDrafts[selectedUser.user_id] || selectedUser.approval_note || ''}
                    onChange={(e) =>
                      setNoteDrafts((prev) => ({
                        ...prev,
                        [selectedUser.user_id]: e.target.value
                      }))
                    }
                  />
                </div>

                {!isProcessed && (
                  <div className="pt-4 border-t border-gray-200">
                    <h4 className="text-sm font-bold text-gray-900 mb-3">심사 처리</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleReject(selectedUser.user_id)}
                        className="w-full py-3 rounded-xl border border-red-200 bg-white text-red-600 font-bold text-sm hover:bg-red-50 transition shadow-sm"
                      >
                        거절 (반려)
                      </button>
                      <button
                        onClick={() => handleApprove(selectedUser)}
                        className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition shadow-blue-200 shadow-md"
                      >
                        승인 완료
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-3 text-center">
                      * 승인 시 해당 회원은 즉시 서비스를 이용할 수 있습니다.
                    </p>
                  </div>
                )}

                {isProcessed && (
                  <div className="pt-4 border-t border-gray-200">
                    <span className={`block w-full text-center py-3 rounded-xl font-bold text-sm select-none
                         ${selectedUser.approval_status === 'approved'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-rose-100 text-rose-800'}`}>
                      {selectedUser.approval_status === 'approved' ? '✅ 승인 완료된 회원' : '🚫 거절된 회원'}
                    </span>
                  </div>
                )}
              </div>

            </div>
          </div>
        )}
      </div>
    )
  }

  // 🔹 전체 매칭(지원) 목록 테이블 + 클릭 시 상세보기
  const renderApplicationsTable = () => {
    if (!applications || applications.length === 0) {
      return (
        <p className="text-sm text-gray-500 px-2 py-3">
          아직 접수된 지원(매칭) 내역이 없습니다.
        </p>
      )
    }

    const sorted = [...applications].sort((a, b) => {
      if (!a.created_at || !b.created_at) return 0
      return new Date(b.created_at) - new Date(a.created_at)
    })

    return (
      <>
        <div className="rounded-2xl border bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b bg-gray-50 text-xs text-gray-500">
                <th className="px-3 py-2 text-left">공고 제목</th>
                <th className="px-3 py-2 text-left">근무일시 / 지역</th>
                <th className="px-3 py-2 text-left">지원자</th>
                <th className="px-3 py-2 text-left">지원일</th>
                <th className="px-3 py-2 text-left">상태</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((app) => {
                const jobTitle = app.job_title || app.job_posts?.title || '(삭제된 공고)'
                const jobDate = app.job_date || app.job_posts?.date || '-'
                const jobTime = app.job_time || app.job_posts?.time || '-'
                const jobRegion = app.job_region || app.job_posts?.region || ''
                const jobDistrict = app.job_district || app.job_posts?.district || ''
                const jobLocation = app.job_location || app.job_posts?.location || ''

                const pharmName =
                  app.pharmacist_profile?.display_name ||
                  app.pharmacist_profile?.name ||
                  app.pharmacist_name ||
                  '(이름 없음)'
                const pharmEmail =
                  app.pharmacist_profile?.email || app.pharmacist_email || ''

                return (
                  <tr
                    key={app.id}
                    className="border-b last:border-b-0 align-top cursor-pointer hover:bg-slate-50"
                    onClick={() => setSelectedApplication(app)}
                  >
                    <td className="px-3 py-2 align-middle text-xs sm:text-sm">
                      {jobTitle}
                    </td>
                    <td className="px-3 py-2 align-middle text-xs text-gray-600">
                      <div>{jobDate} · {jobTime}</div>
                      <div className="text-[11px] text-gray-500">
                        {jobRegion} {jobDistrict} {jobLocation && `(${jobLocation})`}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-middle text-xs sm:text-sm">
                      <div>{pharmName}</div>
                      {pharmEmail && (
                        <div className="text-[11px] text-gray-500">{pharmEmail}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-middle text-xs text-gray-500">
                      {app.created_at
                        ? new Date(app.created_at).toLocaleString()
                        : '-'}
                    </td>
                    <td className="px-3 py-2 align-middle text-xs sm:text-sm">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs
                          ${app.status === 'approved'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : app.status === 'closed'
                              ? 'bg-gray-100 text-gray-600 border-gray-200'
                              : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                          }`}
                      >
                        {app.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* 🔍 매칭 상세 패널 */}
        {selectedApplication && (
          <div className="mt-4 rounded-2xl border bg-white p-4 text-xs sm:text-sm">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold">선택한 매칭 상세 정보</h4>
              <button
                className="text-[11px] text-gray-500 underline"
                onClick={() => setSelectedApplication(null)}
              >
                닫기
              </button>
            </div>
            {(() => {
              const app = selectedApplication
              const jobTitle = app.job_title || app.job_posts?.title || '(삭제된 공고)'
              const jobDate = app.job_date || app.job_posts?.date || '-'
              const jobTime = app.job_time || app.job_posts?.time || '-'
              const jobRegion = app.job_region || app.job_posts?.region || ''
              const jobDistrict = app.job_district || app.job_posts?.district || ''
              const jobLocation = app.job_location || app.job_posts?.location || ''
              const jobDesc = app.job_posts?.description || app.job_description || ''

              const pharmName =
                app.pharmacist_profile?.display_name ||
                app.pharmacist_profile?.name ||
                app.pharmacist_name ||
                '(이름 없음)'
              const pharmEmail =
                app.pharmacist_profile?.email || app.pharmacist_email || ''
              const contact = app.contact || ''

              return (
                <div className="grid gap-2">
                  <div>
                    <div className="text-[11px] text-gray-500 mb-0.5">공고 정보</div>
                    <div className="font-semibold">{jobTitle}</div>
                    <div className="text-gray-600">
                      {jobDate} · {jobTime}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {jobRegion} {jobDistrict} {jobLocation && `(${jobLocation})`}
                    </div>
                    {jobDesc && (
                      <div className="mt-1 text-gray-700 whitespace-pre-line">
                        {jobDesc}
                      </div>
                    )}
                  </div>

                  <div className="mt-2">
                    <div className="text-[11px] text-gray-500 mb-0.5">지원자</div>
                    <div>{pharmName}</div>
                    {pharmEmail && (
                      <div className="text-[11px] text-gray-500">{pharmEmail}</div>
                    )}
                    {contact && (
                      <div className="text-[11px] text-gray-500">연락처: {contact}</div>
                    )}
                  </div>

                  <div className="mt-2">
                    <div className="text-[11px] text-gray-500 mb-0.5">지원 정보</div>
                    <div>
                      지원일:{' '}
                      {app.created_at
                        ? new Date(app.created_at).toLocaleString()
                        : '-'}
                    </div>
                    <div className="mt-1">
                      상태:{' '}
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs
                          ${app.status === 'approved'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : app.status === 'closed'
                              ? 'bg-gray-100 text-gray-600 border-gray-200'
                              : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                          }`}
                      >
                        {app.status || 'pending'}
                      </span>
                    </div>
                    {app.request_message && (
                      <div className="mt-2">
                        <div className="text-[11px] text-gray-500 mb-0.5">
                          지원 메시지
                        </div>
                        <div className="whitespace-pre-line text-gray-700">
                          {app.request_message}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
        )}
      </>
    )
  }

  return (
    <div className="grid gap-6">
      {/* 1) 가입 승인 관리 섹션 */}
      <div>
        <h2 className="text-lg font-semibold mb-1">가입 승인 관리</h2>
        <p className="text-xs text-gray-500">
          신규로 가입한 약사/약국 계정을 확인하고 승인 또는 거절할 수 있는 화면입니다.
          <br />
          승인/거절 사유는 &quot;관리자 메모&quot;에 남겨두면 나중에 내부 공유/기록으로 활용할 수 있습니다.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* 필터 바 */}
      <div className="flex flex-wrap gap-2 items-center bg-white border rounded-lg px-3 py-2 text-xs shadow-sm">
        <div className="flex items-center gap-1">
          <span className="text-gray-600">검색:</span>
          <input
            className="border rounded px-2 py-1 text-xs w-40"
            placeholder="이메일 / 이름 / 역할"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-600">역할:</span>
          <select
            className="border rounded px-2 py-1 text-xs"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">전체</option>
            <option value="pharmacist">약사</option>
            <option value="pharmacy">약국</option>
            <option value="admin">관리자</option>
          </select>
        </div>
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-3 py-1 rounded-full border text-xs ${activeTab === 'pending'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300'
              }`}
          >
            승인 대기
          </button>
          <button
            onClick={() => setActiveTab('processed')}
            className={`px-3 py-1 rounded-full border text-xs ${activeTab === 'processed'
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-700 border-gray-300'
              }`}
          >
            처리 완료 내역
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-gray-500">로딩 중…</div>
      )}

      {!loading && activeTab === 'pending' && renderUserTable(filteredPending, false)}
      {!loading && activeTab === 'processed' && renderUserTable(filteredProcessed, true)}

      <div className="mt-2 flex justify-end">
        <button
          onClick={loadAll}
          className="rounded-md border px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          새로고침
        </button>
      </div>

      {/* 2) 공고 / 매칭 현황 섹션 */}
      <div className="mt-4">
        <h2 className="text-lg font-semibold mb-1">공고 및 매칭 현황</h2>
        <p className="text-xs text-gray-500 mb-2">
          현재 FLOATER 내 등록된 공고와 지원(매칭) 현황을 관리자 계정에서 한 번에 확인할 수 있는 대시보드입니다.
          매칭 목록 행을 클릭하면 세부 내용을 아래에서 확인할 수 있습니다.
        </p>

        {/* 공고 요약 통계 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div className="rounded-xl border bg-white p-3">
            <div className="text-xs text-gray-500 mb-1">전체 공고 수</div>
            <div className="text-xl font-bold text-gray-800">
              {jobStats.total}
            </div>
          </div>
          <div className="rounded-xl border bg-white p-3">
            <div className="text-xs text-gray-500 mb-1">모집중 공고 (open)</div>
            <div className="text-xl font-bold text-emerald-700">
              {jobStats.open}
            </div>
          </div>
          <div className="rounded-xl border bg-white p-3">
            <div className="text-xs text-gray-500 mb-1">마감 공고 (closed)</div>
            <div className="text-xl font-bold text-gray-600">
              {jobStats.closed}
            </div>
          </div>
        </div>

        {/* 전체 매칭(지원) 목록 + 상세보기 */}
        <div className="mt-2">
          <h3 className="text-sm font-semibold mb-2">전체 매칭(지원) 목록</h3>
          {renderApplicationsTable()}
        </div>
      </div>
    </div>
  )
}
